<template>
  <v-navigation-drawer
    id="core-navigation-drawer"
    v-model="drawer"
    :dark="barColor !== 'rgba(228, 226, 226, 1), rgba(255, 255, 255, 0.7)'"
    :expand-on-hover="expandOnHover"
    :right="$vuetify.rtl"
    :src="barImage"
    mobile-break-point="960"
    app
    mini-variant-width="80"
    width="260"
    v-bind="$attrs"
  >
    <template v-slot:img="props">
      <v-img
        :gradient="`to bottom, ${barColor}`"
        v-bind="props"
      />
    </template>

    <!-- Entête Happy Quest -->
    <v-list-item two-line>
      <v-list-item-content>
        <v-list-item-title class="text-uppercase font-weight-regular display-2">
          <span class="logo-mini">
            <v-img :src="require('../../../../../public/happyquest.png')" />
          </span>
          <span class="logo-normal">{{ $t('happyQuest') }}</span>
        </v-list-item-title>
      </v-list-item-content>
    </v-list-item>

    <v-divider class="mb-1" />

    <!-- Entête du profil -->
    <v-list-item>
      <v-list-item-content>
        <v-list-item-title class="text-uppercase font-weight-regular display-2 text-center">
          <span class="logo-normal">{{ companyDetails.name }}</span>
        </v-list-item-title>
        <v-row justify="center">
          ({{ $Auth.getProfile().sub }})
        </v-row>
      </v-list-item-content>
    </v-list-item>

    <v-divider class="mb-2" />

    <v-list
      expand
      nav
    >
      <!-- Style cascading bug  -->
      <!-- https://github.com/vuetifyjs/vuetify/pull/8574 -->
      <div />

      <template v-for="(item, i) in computedItems">
        <template
          v-if="!!item.divider"
        >
          <v-divider
            :key="`divider-${i}`"
            class="mb-2"
          />
        </template>
        <template v-else>
          <base-item-group
            v-if="item.children"
            :key="`group-${i}`"
            :item="item"
          >
            <!--  -->
          </base-item-group>

          <base-item
            v-else
            :key="`item-${i}`"
            :item="item"
            :disabled="item.disabled"
          />
        </template>
      </template>

      <!-- Style cascading bug  -->
      <!-- https://github.com/vuetifyjs/vuetify/pull/8574 -->
      <div />
    </v-list>
  </v-navigation-drawer>
</template>

<script>
  // Utilities
  import {
    mapState,
  } from 'vuex'

  export default {
    name: 'DashboardCoreDrawer',

    props: {
      expandOnHover: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      items: [
        {
          icon: 'mdi-view-dashboard',
          title: 'Drawer.Dashboard',
          to: '/',
        },
        {
          icon: 'mdi-label-multiple',
          title: 'Drawer.TagsList',
          to: '/tags',
        },
        {
          icon: 'mdi-brain',
          title: 'Drawer.Training',
          to: '/#training',
        },
        {
          icon: 'mdi-account-heart',
          title: 'Drawer.Measure',
          to: '/#measure',
        },
        {
          divider: true,
        },
      ],
      companyDetails: '-',
      companiesDetails: {},
    }),

    computed: {
      ...mapState(['barColor', 'barImage']),
      drawer: {
        get () {
          return this.$store.state.drawer
        },
        set (val) {
          this.$store.commit('SET_DRAWER', val)
        },
      },
      computedItems () {
        return this.items.map(this.mapItem)
      },
      profile () {
        return this.$Auth.getProfile()
      },
    },

    watch: {
      '$vuetify.breakpoint.smAndDown' (val) {
        this.$emit('update:expandOnHover', !val)
      },
    },

    /* Hook : fonction appelée juste après que l’instance ait été montée */
    mounted: function () {
      this.fetchDataCompanyDetails()
      if (this.$Auth.isAdmin()) {
        this.fetchCompaniesData()
      }
    },

    /* Fonctions internes */
    methods: {
      mapItem: function (item) {
        return {
          ...item,
          children: item.children ? item.children.map(this.mapItem) : undefined,
          title: item.avoidI18nCheck
            ? item.title
            : (item.titleVariant
              ? this.$tc(item.titleVariant, 2)
              : this.$t(item.title)),
        }
      },

      // Récupération des informations de l'entreprise de l'utilisateur
      fetchDataCompanyDetails: function () {
        return this.$Auth.getCompanyDetails(this.profile.sub)
          .then(datas => {
            this.companyDetails = datas
          })
          .catch((/* error */) => {
            if (this.$Auth.isAdmin()) {
              // console.log('Error in ' + this.$options.name + ' : ', error)
            }
          })
      },

      // Récupération des informations de toutes les entreprises
      fetchCompaniesData: function () {
        this.$Auth.httpRequest('get', 'api/dashboard/admin/selectCompanies')
          .then(datas => {
            if (
              !Array.isArray(datas.data) ||
              (Array.isArray(datas.data) && datas.data.length < 1)
            ) {
              return
            }
            const adminNavItems = {
              items: [],
            }
            Object.keys(datas.data).forEach(function (key) {
              adminNavItems.items.push({
                title: datas.data[key].name,
                to: datas.data[key].id,
                avoidI18nCheck: true,
              })
            })
            const newData = this.items
            // Ajout de la page 'liste des entreprises' au drawer
            newData.push({
              title: 'Drawer.CompaniesList',
              icon: 'mdi-text-box-multiple',
              to: '/admin/companies',
            })
            // Ajout de la page 'liste des mondes d'une entreprise' au drawer
            newData.push({
              titleVariant: 'Drawer.CompanyWorldsList',
              icon: 'mdi-text-box-multiple',
              to: '/admin/companyworlds',
              disabled: true,
            })
            // Ajout des pages de dashboard des entreprises au drawer
            newData.push({
              title: 'companies',
              icon: 'mdi-view-dashboard',
              // Les enfants d'un group voit leur champ 'to' transformé en 'group' du parent + 'to'
              group: '/admin/dashboard',
              children: adminNavItems.items,
            })
            // Ajout de la page 'liste des mondes' au drawer
            newData.push({
              title: 'worldsList',
              icon: 'mdi-text-box-multiple',
              to: '/admin/worlds',
            })
            // Ajout de la page 'liste des ressources d'un monde' au drawer
            /*
            newData.push({
              titleVariant: 'Drawer.WorldResourcesList',
              icon: 'mdi-text-box-multiple',
              to: '/admin/worldresources',
              disabled: true,
            })
            */
            // Ajout de la page 'liste des ressources d'un monde' au drawer
            newData.push({
              titleVariant: 'Drawer.Users',
              icon: 'mdi-account-multiple',
              to: '/admin/users',
            })
            this.items = newData
            //  on sauvegarde également l'ensemble des données liés aux Entreprises
            this.companiesDetails = datas
          })
      },
    },
  }
</script>

<style lang="sass">
  @import '~vuetify/src/styles/tools/_rtl.sass'

  #core-navigation-drawer
    &.v-navigation-drawer--mini-variant
      .v-list-item
        justify-content: flex-start !important

      .v-list-group--sub-group
        display: block !important

    .v-list-group__header.v-list-item--active:before
      opacity: .24

    .v-list-item
      &__icon--text,
      &__icon:first-child
        justify-content: center
        text-align: center
        width: 20px

        +ltr()
          margin-right: 24px
          margin-left: 12px !important

        +rtl()
          margin-left: 24px
          margin-right: 12px !important

    .v-list--dense
      .v-list-item
        &__icon--text,
        &__icon:first-child
          margin-top: 10px

    .v-list-group--sub-group
      .v-list-item
        +ltr()
          padding-left: 8px

        +rtl()
          padding-right: 8px

      .v-list-group__header
        +ltr()
          padding-right: 0

        +rtl()
          padding-right: 0

        .v-list-item__icon--text
          margin-top: 19px
          order: 0

        .v-list-group__header__prepend-icon
          order: 2

          +ltr()
            margin-right: 8px

          +rtl()
            margin-left: 8px
</style>
